import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { KitRelabelUnit } from '~/shared/api/services/delivery/types'
import { useStore } from '~/app/store'
import { Button, Card, PageTitle, Switcher } from '~/shared/ui'
import { ModalWindow } from '~/shared/ui/ModalWindow'
import { TableContainer } from '~/shared/ui/table'

import { DELIVERY_TABLE_CONFIG } from './lib/configTable'
import { Input } from '~/shared/ui/input'
import { ModalMandatorymarkField } from './ModalMandatorymarkField'
import { LEGAL_ENTITY_GREY_SHOP, useToast } from '~/shared/lib'
import {
  getKitNewSkuContextKey,
  getKitNewSkuModalReset,
  syncKitNewSkuModalState,
  type KitNewSkuModalReset,
} from '../lib/order-request-guard'

interface OrdersItemsTableProps {
  onLastItemChecked?: () => void
  canScanLastItem?: boolean
  capsLockEnabled?: boolean
  kitRelabelRequired?: boolean
  kitRelabelUnit?: KitRelabelUnit | null
  onKitComponentScanned?: (productVariationId: string) => boolean
  onKitNewSkuScanned?: (barcode: string) => Promise<boolean>
}

export const OrdersItemsTable = observer(
  ({
    onLastItemChecked,
    canScanLastItem = true,
    capsLockEnabled = false,
    kitRelabelRequired = false,
    kitRelabelUnit = null,
    onKitComponentScanned,
    onKitNewSkuScanned,
  }: OrdersItemsTableProps) => {
    const { deliveryStore, usersStore } = useStore()
    const {
      itemsOrderById,
      loaders,
      getProductByBarcode,
      setBarcode,
      barcode,
      modalMandatoryMarkField,
      setOrderItems,
    } = deliveryStore

    const { userProfile } = usersStore

    const { t } = useTranslation(['common', 'delivery'])
    const { id } = useParams()
    const kitNewSkuStickerPrinted = kitRelabelUnit?.stickerPrinted === true
    const kitNewSkuContextKey = getKitNewSkuContextKey({
      orderId: id,
      kitVariationId: kitRelabelUnit?.kitVariationId,
      unitIndex: kitRelabelUnit?.unitIndex,
      stickerPrinted: kitNewSkuStickerPrinted,
    })
    const barcodeInputRef = useRef<HTMLInputElement>(null)
    const modalOpenRef = useRef(modalMandatoryMarkField.open)
    const orderIdRef = useRef(id)
    const barcodeRequestRef = useRef<symbol | null>(null)
    const kitRelabelUnitRef = useRef(kitRelabelUnit)
    const [barcodeModeDelivery, setBarcodeModeDelivery] = useState(false)
    const barcodeModeRef = useRef(barcodeModeDelivery)
    const toast = useToast()
    const [kitNewSkuModalState, setKitNewSkuModalState] = useState(() =>
      getKitNewSkuModalReset(kitNewSkuStickerPrinted, kitNewSkuContextKey),
    )
    const currentKitNewSkuModalState = syncKitNewSkuModalState(
      kitNewSkuModalState,
      kitNewSkuContextKey,
      kitNewSkuStickerPrinted,
    )
    const kitNewSkuInputRef = useRef<HTMLInputElement>(null)
    const kitNewSkuModalOpenRef = useRef(currentKitNewSkuModalState.isOpen)
    const kitNewSkuRequestRef = useRef<symbol | null>(null)

    modalOpenRef.current = modalMandatoryMarkField.open
    kitNewSkuModalOpenRef.current = currentKitNewSkuModalState.isOpen
    orderIdRef.current = id
    barcodeModeRef.current = barcodeModeDelivery
    kitRelabelUnitRef.current = kitRelabelUnit

    useEffect(() => {
      kitNewSkuRequestRef.current = null
      setKitNewSkuModalState((state) =>
        syncKitNewSkuModalState(
          state,
          kitNewSkuContextKey,
          kitNewSkuStickerPrinted,
        ),
      )
    }, [
      id,
      kitNewSkuContextKey,
      kitNewSkuStickerPrinted,
      kitRelabelUnit?.kitVariationId,
      kitRelabelUnit?.unitIndex,
      kitRelabelUnit?.stickerPrinted,
    ])

    const updateKitNewSkuModalState = (
      update: Partial<KitNewSkuModalReset>,
    ) => {
      setKitNewSkuModalState((state) => ({
        ...syncKitNewSkuModalState(
          state,
          kitNewSkuContextKey,
          kitNewSkuStickerPrinted,
        ),
        ...update,
      }))
    }

    useEffect(() => {
      if (currentKitNewSkuModalState.isOpen) {
        kitNewSkuInputRef.current?.focus()
      }
    }, [currentKitNewSkuModalState.isOpen])

    const isLastUncheckedItem = (itemId: string) =>
      itemsOrderById.filter((item) => !item.checked).length === 1 &&
      itemsOrderById.some((item) => item.id === itemId && !item.checked)

    let barcodeLabel = t('delivery:barcode')
    if (kitRelabelUnit) {
      barcodeLabel = kitRelabelUnit.stickerPrinted
        ? t('delivery:scanNewKitBarcode')
        : t('delivery:scanKitComponentBarcode')
    }

    const focusBarcodeInput = useCallback(() => {
      const input = barcodeInputRef.current
      if (
        capsLockEnabled ||
        modalOpenRef.current ||
        kitNewSkuModalOpenRef.current ||
        !barcodeModeRef.current ||
        loaders.barcode ||
        loaders.shippingOrderById ||
        !input ||
        input.disabled ||
        !input.isConnected ||
        input.getClientRects().length === 0
      ) {
        return
      }

      input.focus()
    }, [capsLockEnabled, loaders.barcode, loaders.shippingOrderById])

    useEffect(() => {
      if (userProfile?.legalEntity?.id) {
        setBarcodeModeDelivery(
          userProfile.legalEntity.id === LEGAL_ENTITY_GREY_SHOP,
        )
      }
    }, [userProfile])

    useEffect(() => {
      barcodeRequestRef.current = null
    }, [id])

    useEffect(() => {
      if (barcodeModeDelivery) focusBarcodeInput()
    }, [barcodeModeDelivery, capsLockEnabled, focusBarcodeInput])

    useEffect(() => {
      if (!modalMandatoryMarkField.open && !currentKitNewSkuModalState.isOpen) {
        focusBarcodeInput()
      }
    }, [
      modalMandatoryMarkField.open,
      currentKitNewSkuModalState.isOpen,
      focusBarcodeInput,
    ])

    return (
      <Card>
        <div className=" flex flex-col gap-3 w-[50%]  p-4">
          <div className=" py-3 w-[50%] flex ">
            <Switcher
              defaultValue={barcodeModeDelivery}
              disabled={capsLockEnabled}
              onChange={(value) => {
                setBarcodeModeDelivery(value)
              }}
            />
            <p> {t('searchByBarcode')}</p>
          </div>
          {barcodeModeDelivery && (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (capsLockEnabled) return
                if (kitNewSkuModalOpenRef.current) return
                if (kitRelabelUnitRef.current?.stickerPrinted) {
                  updateKitNewSkuModalState({ isOpen: true })
                  return
                }
                if (barcode) {
                  if (barcodeRequestRef.current) return
                  const requestToken = Symbol('barcode-search')
                  barcodeRequestRef.current = requestToken
                  const requestOrderId = id
                  const requestKitRelabelUnit = kitRelabelUnit
                  try {
                    if (
                      orderIdRef.current !== requestOrderId ||
                      kitRelabelUnitRef.current?.kitVariationId !==
                        requestKitRelabelUnit?.kitVariationId ||
                      kitRelabelUnitRef.current?.unitIndex !==
                        requestKitRelabelUnit?.unitIndex ||
                      kitRelabelUnitRef.current?.stickerPrinted !==
                        requestKitRelabelUnit?.stickerPrinted
                    ) {
                      return
                    }

                    if (requestKitRelabelUnit?.stickerPrinted) {
                      await onKitNewSkuScanned?.(barcode)
                      return
                    }

                    const data = await getProductByBarcode(barcode)
                    if (
                      orderIdRef.current !== requestOrderId ||
                      kitRelabelUnitRef.current?.kitVariationId !==
                        requestKitRelabelUnit?.kitVariationId ||
                      kitRelabelUnitRef.current?.unitIndex !==
                        requestKitRelabelUnit?.unitIndex ||
                      kitRelabelUnitRef.current?.stickerPrinted !==
                        requestKitRelabelUnit?.stickerPrinted
                    ) {
                      return
                    }

                    if (requestKitRelabelUnit) {
                      if (onKitComponentScanned?.(data.id)) {
                        toast.open(t('delivery:kitComponentScanned'), 'success')
                      }
                      return
                    }

                    const nextItem = itemsOrderById.find(
                      (item) =>
                        item.productVariationId === data.id && !item.checked,
                    )
                    const findItemInOrder = itemsOrderById.find(
                      (item) => item.productVariationId === data.id,
                    )

                    if (nextItem) {
                      toast.open(t('delivery:productFoundByBarcode'), 'success')
                      const isLastItem = isLastUncheckedItem(nextItem.id)

                      if (isLastItem && !canScanLastItem) {
                        toast.open(
                          t('delivery:confirmKitBarcodeRelabelFirst'),
                          'danger',
                        )
                        return
                      }

                      if (nextItem.mandatoryMarkField) {
                        modalOpenRef.current = true
                        deliveryStore.modalMandatoryMarkField = {
                          open: true,
                          id: nextItem.id,
                          mandatoryMarkField: nextItem.mandatoryMarkField,
                        }
                      } else {
                        setOrderItems(nextItem.id, 'checked', true)

                        if (isLastItem) {
                          onLastItemChecked?.()
                        }
                      }
                    } else if (findItemInOrder) {
                      toast.open(
                        t('delivery:allProductsWithBarcodeMarked'),
                        'danger',
                      )
                    } else {
                      toast.open(t('delivery:productNotInOrder'), 'danger')
                    }
                  } catch {
                    if (orderIdRef.current === requestOrderId) {
                      toast.open(t('delivery:productNotFound'), 'danger')
                    }
                  } finally {
                    if (barcodeRequestRef.current === requestToken) {
                      barcodeRequestRef.current = null
                    }
                    if (orderIdRef.current === requestOrderId) {
                      deliveryStore.resetBarcode()
                      focusBarcodeInput()
                    }
                  }
                }
              }}
            >
              <Input
                labelType="floating"
                label={barcodeLabel}
                className="rounded-lg"
                id="barcode"
                disabled={
                  capsLockEnabled ||
                  currentKitNewSkuModalState.isOpen ||
                  kitRelabelUnit?.stickerPrinted === true ||
                  loaders.barcode ||
                  loaders.shippingOrderById
                }
                value={barcode}
                ref={barcodeInputRef}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (capsLockEnabled) return
                  setBarcode(e.target.value)
                }}
              />
            </form>
          )}
          {kitRelabelUnit?.stickerPrinted &&
            !currentKitNewSkuModalState.isOpen && (
              <Button
                type="button"
                intent="primary"
                onClick={() => updateKitNewSkuModalState({ isOpen: true })}
              >
                {t('delivery:scanNewKitBarcode')}
              </Button>
            )}
        </div>
        <div className="w-full">
          <TableContainer
            fixedWidth
            isLoading={loaders.shippingOrderById || loaders.barcode}
            columns={DELIVERY_TABLE_CONFIG}
            data={itemsOrderById}
            onRowClick={(item) => {
              if (kitRelabelRequired && item.kitVariationId) {
                toast.open(t('delivery:scanKitComponentInstead'), 'danger')
                return
              }

              if (item.checked) {
                setOrderItems(item.id, 'checked', false)
                setOrderItems(item.id, 'markValue', '')
                return
              }

              if (isLastUncheckedItem(item.id) && !canScanLastItem) {
                toast.open(
                  t('delivery:confirmKitBarcodeRelabelFirst'),
                  'danger',
                )
                return
              }

              if (item.mandatoryMarkField) {
                modalOpenRef.current = true
                deliveryStore.modalMandatoryMarkField = {
                  open: true,
                  id: item.id,
                  mandatoryMarkField: item.mandatoryMarkField,
                }
              } else {
                const shouldTriggerAutoPrint =
                  !item.checked && isLastUncheckedItem(item.id)

                setOrderItems(item.id, 'checked', true)

                if (shouldTriggerAutoPrint) {
                  onLastItemChecked?.()
                }
              }
            }}
            className="bg-gray-300"
          />
        </div>
        <ModalMandatorymarkField
          orderId={id}
          isOpen={modalMandatoryMarkField.open}
          id={modalMandatoryMarkField.id}
          originalData={modalMandatoryMarkField.mandatoryMarkField}
          capsLockEnabled={capsLockEnabled}
          onClose={(e) => {
            e.preventDefault()
            modalOpenRef.current = false
            deliveryStore.modalMandatoryMarkField = {
              open: false,
              id: '',
              mandatoryMarkField: '',
            }
            deliveryStore.inputMandatoryMark = ''
          }}
          onLastItemChecked={onLastItemChecked}
        />
        <ModalWindow
          isOpen={
            currentKitNewSkuModalState.isOpen &&
            kitRelabelUnit?.stickerPrinted === true
          }
          maxWidth
          onClose={() => {
            updateKitNewSkuModalState({ isOpen: false, barcode: '' })
          }}
        >
          <form
            className="flex w-[400px] max-w-full flex-col gap-4"
            onSubmit={async (event) => {
              event.preventDefault()
              const scannedBarcode = currentKitNewSkuModalState.barcode.trim()
              if (!scannedBarcode || currentKitNewSkuModalState.isSubmitting)
                return

              const requestOrderId = id
              const requestToken = Symbol('kit-new-sku')
              kitNewSkuRequestRef.current = requestToken
              updateKitNewSkuModalState({ isSubmitting: true })
              try {
                const confirmed = await onKitNewSkuScanned?.(scannedBarcode)
                if (
                  orderIdRef.current !== requestOrderId ||
                  kitNewSkuRequestRef.current !== requestToken
                ) {
                  return
                }
                updateKitNewSkuModalState({ barcode: '' })
                if (confirmed) updateKitNewSkuModalState({ isOpen: false })
              } finally {
                if (
                  orderIdRef.current === requestOrderId &&
                  kitNewSkuRequestRef.current === requestToken
                ) {
                  updateKitNewSkuModalState({ isSubmitting: false })
                }
              }
            }}
          >
            <PageTitle
              title={t('delivery:scanNewKitBarcode')}
              size="text-[20px]"
            />
            <Input
              ref={kitNewSkuInputRef}
              id="kit-new-sku-barcode"
              labelType="floating"
              label={t('delivery:scanNewKitBarcode')}
              value={currentKitNewSkuModalState.barcode}
              disabled={currentKitNewSkuModalState.isSubmitting}
              autoComplete="off"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                updateKitNewSkuModalState({ barcode: event.target.value })
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                intent="secondary"
                onClick={() => {
                  updateKitNewSkuModalState({ isOpen: false, barcode: '' })
                }}
              >
                {t('common:Cancel2')}
              </Button>
              <Button
                type="submit"
                intent="success"
                isLoading={currentKitNewSkuModalState.isSubmitting}
                disabled={!currentKitNewSkuModalState.barcode.trim()}
              >
                {t('common:Confirm')}
              </Button>
            </div>
          </form>
        </ModalWindow>
      </Card>
    )
  },
)
